import { useEffect, useRef, useCallback } from 'react'
import { X } from 'lucide-react'

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'default',
  children,
  footer,
  className,
}) {
  const modalRef = useRef(null)
  const previousActiveElement = useRef(null)

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  // Handle click outside
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }, [onClose])

  // Focus management and body scroll lock
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKeyDown)
      
      // Focus the modal
      setTimeout(() => {
        modalRef.current?.focus()
      }, 0)
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
      
      // Restore focus
      previousActiveElement.current?.focus()
    }

    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  console.log('[Modal] Rendering modal with title:', title)

  const sizeClasses = {
    default: '',
    lg: 'modal-lg',
    xl: 'modal-xl',
  }

  return (
    <div 
      className="modal-overlay" 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgb(var(--rgb-shade) / 0.8)',
        backdropFilter: 'blur(4px)'
      }}
    >
      <div 
        ref={modalRef}
        className={`modal ${sizeClasses[size]} ${className || ''}`}
        tabIndex={-1}
        style={{
          position: 'relative',
          zIndex: 500,
          maxWidth: size === 'lg' ? '700px' : size === 'xl' ? '900px' : '500px',
          width: '100%'
        }}
      >
        <div className="modal-header">
          <div>
            <h3 id="modal-title">{title}</h3>
            {subtitle && <p className="text-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>{subtitle}</p>}
          </div>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {children}
        </div>
        
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
